/**
 * The strip across the top of every page in a sandbox build (ADR sandbox-before-live,
 * docs/ENVIRONMENTS.md). Says "Sandbox", so a review copy is never mistaken
 * for the live app, and — for people who can read the change log — lists the
 * changes in this build that production does not have yet, which is exactly
 * what is waiting for Gideon's approval.
 *
 * Not dismissible: the whole point is that it cannot be forgotten. Production
 * never renders it. It reads no records and ignores Test mode.
 */
import { Link } from '@tanstack/react-router'
import { ChevronDown, Construction } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import type { WaitingChange } from '@/core/system/build'
import { useWaitingChanges } from '../api'

export function SandboxBanner({ showChanges }: { showChanges: boolean }) {
  return (
    // Opaque, so content scrolling underneath never shows through.
    <section
      aria-label="Sandbox"
      data-slot="sandbox-banner"
      className="flex h-11 shrink-0 items-center gap-3 border-b border-sandbox-foreground/15 bg-sandbox px-4 text-sandbox-foreground"
    >
      <Construction className="size-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1 truncate text-sm">
        {/* One word (D-035): the colour and the list say the rest. */}
        <span className="font-semibold">Sandbox</span>
      </p>
      {showChanges ? <WaitingChanges /> : null}
    </section>
  )
}

function triggerLabel(state: ReturnType<typeof useWaitingChanges>) {
  if (state.isPending) return { short: 'Checking…', long: 'Checking production…' }
  if (state.isError || state.data?.status !== 'ok') {
    return { short: "Can't reach production", long: "Couldn't reach production" }
  }
  const count = state.data.changes.length
  if (count === 0) return { short: 'Nothing waiting', long: 'Nothing waiting' }
  return {
    short: `${count} waiting`,
    long: `${count} ${count === 1 ? 'change' : 'changes'} waiting`,
  }
}

function WaitingChanges() {
  const [open, setOpen] = useState(false)
  const waiting = useWaitingChanges(true)
  const label = triggerLabel(waiting)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            className="-mr-2 gap-1 px-3 text-sm text-sandbox-foreground hover:bg-sandbox-foreground/10 hover:text-sandbox-foreground dark:hover:bg-sandbox-foreground/10 aria-expanded:bg-sandbox-foreground/10 aria-expanded:text-sandbox-foreground"
          />
        }
      >
        <span className="md:hidden">{label.short}</span>
        <span className="hidden md:inline">{label.long}</span>
        <ChevronDown className="size-4" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={0}
        className="w-[min(26rem,calc(100vw-2rem))] gap-4 p-0"
      >
        <PopoverHeader className="px-4 pt-4">
          <PopoverTitle>Waiting for approval</PopoverTitle>
        </PopoverHeader>
        <WaitingBody state={waiting} onOpenEntry={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}

function WaitingBody({
  state,
  onOpenEntry,
}: {
  state: ReturnType<typeof useWaitingChanges>
  onOpenEntry: () => void
}) {
  if (state.isPending) {
    return <p className="px-4 pb-4 text-sm text-muted-foreground">Asking production…</p>
  }

  if (state.isError || state.data?.status !== 'ok') {
    return (
      <div className="flex flex-col items-start gap-3 px-4 pb-4">
        <p className="text-sm text-muted-foreground">
          {state.isError
            ? "Couldn't check what's waiting."
            : "Production didn't answer, so there's no telling which of these changes are live yet."}
        </p>
        <Button variant="outline" onClick={() => void state.refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  if (state.data.changes.length === 0) {
    return (
      <p className="px-4 pb-4 text-sm text-muted-foreground">
        Nothing waiting. Everything in this sandbox is already live.
      </p>
    )
  }

  return (
    <ul className="flex max-h-[min(24rem,60dvh)] flex-col gap-1 overflow-y-auto px-2 pb-2">
      {state.data.changes.map((change) => (
        <li key={change.slug}>
          <WaitingEntry change={change} onOpen={onOpenEntry} />
        </li>
      ))}
    </ul>
  )
}

function WaitingEntry({ change, onOpen }: { change: WaitingChange; onOpen: () => void }) {
  return (
    <Link
      to="/settings/system"
      search={{ tab: 'change' }}
      hash={`change-${change.slug}`}
      onClick={onOpen}
      className="flex min-h-11 flex-col gap-1 rounded-lg px-3 py-2 outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <span className="text-sm font-semibold">{change.title}</span>
      <span className="line-clamp-2 text-sm text-muted-foreground">{change.summary}</span>
    </Link>
  )
}
